import React, { useEffect, useState } from 'react';
import { IMultimedia } from '../../Models/Multimedia/IMultimedia';
import { IMultimediaFilterParams } from '../../Models/Multimedia/IMultimediaFilterParams';
import { MultimediaService } from '../../Services';

interface Props {
    multimedia: IMultimedia;
    unselectMultimedia: () => void;
    selectMedia: (media: IMultimedia) => void;
    dictBackLabel: string;
    dictSimilarMedias: string;
    area: string;
}

export default function MultimediaDetail({
    multimedia,
    unselectMultimedia,
    selectMedia,
    dictBackLabel,
    dictSimilarMedias,
    area,
}: Props) {
    const [similarMedias, setSimilarMedias] = useState<IMultimedia[]>([]);

    useEffect(() => {
        let isMounted = true;

        const loadSimilarMedias = async () => {
            if (!multimedia.tags?.length) {
                setSimilarMedias([]);
                return;
            }

            const tagIds = multimedia.tags
                .map((tag) => Number(tag.id))
                .filter((id) => Number.isFinite(id));

            if (tagIds.length === 0) {
                setSimilarMedias([]);
                return;
            }

            try {
                const params: IMultimediaFilterParams = {
                    page: 0,
                    pageSize: 6,
                    target: area,
                    searchText: null,
                    themeIds: null,
                    tagIds,
                };

                const result =
                    await MultimediaService.getAllMultimedias(params);

                if (isMounted) {
                    const filtered = result.multimediaContents.filter(
                        (media) => media.id !== multimedia.id
                    );

                    setSimilarMedias(filtered);
                }
            } catch (error) {
                console.error('Failed to load similar media:', error);

                if (isMounted) {
                    setSimilarMedias([]);
                }
            }
        };

        void loadSimilarMedias();

        return () => {
            isMounted = false;
        };
    }, [multimedia.id, multimedia.tags, area]);

    const youtubeId = getYoutubeId(multimedia.youtubeLink);
    const date = new Date(multimedia.creationDate);

    const handleBackClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault();
        unselectMultimedia();
    };

    const handleMediaClick =
        (media: IMultimedia) => (e: React.MouseEvent<HTMLAnchorElement>) => {
            e.preventDefault();
            selectMedia(media);
        };

    const renderContent = () => {
        if (multimedia.type.toLowerCase() !== 'video') {
            return (
                <div
                    className="col-12"
                    dangerouslySetInnerHTML={{ __html: multimedia.content }}
                />
            );
        }

        return (
            <>
                <div
                    className="col-12"
                    dangerouslySetInnerHTML={{ __html: multimedia.content }}
                />

                {youtubeId && (
                    <div
                        style={{ paddingTop: '56.25%' }}
                        className="position-relative m-3 w-100"
                    >
                        <iframe
                            width="100%"
                            height="100%"
                            src={`https://www.youtube.com/embed/${youtubeId}`}
                            title="YouTube video"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                            }}
                            allowFullScreen
                        />
                    </div>
                )}
            </>
        );
    };

    return (
        <div className="mb-5">
            <div className="banner banner--content d-flex align-items-center mb-0">
                <div
                    className="banner--content__image"
                    style={{
                        backgroundImage: `url(${multimedia.thumbnailUrl})`,
                    }}
                />

                <div className="banner--content__titlegroup">
                    <div className="banner--content__content">
                        <h1>{multimedia.title}</h1>
                        <h3 className="mb-4">{multimedia.description}</h3>

                        <div className="text-center">
                            {date.toLocaleDateString()}
                            {multimedia.readingTime !== 0 && (
                                <>
                                    &nbsp;- <i className="icon-clock" />{' '}
                                    {multimedia.readingTime}
                                    min
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="container">
                <div className="row">
                    <div className="col-md-8 col-12">
                        <div className="row">
                            <div className="col-12 d-flex justify-content-between">
                                <a
                                    href="#"
                                    onClick={handleBackClick}
                                    className="back-to back-to--small mt-4"
                                >
                                    {dictBackLabel}
                                </a>
                            </div>

                            {renderContent()}
                        </div>
                    </div>

                    <div className="col-md-4 col-12">
                        {similarMedias.length > 0 && (
                            <div className="row">
                                <div className="col-12">
                                    <h1 className="border-bottom mt-3">
                                        {dictSimilarMedias}
                                    </h1>
                                </div>

                                {similarMedias.map((media) => {
                                    const mediaDate = new Date(
                                        media.creationDate
                                    );

                                    return (
                                        <React.Fragment key={media.id}>
                                            <div className="col-4 d-flex align-items-center">
                                                <div
                                                    className="highlighted-img"
                                                    style={{
                                                        background: `url('${media.thumbnailUrl}')`,
                                                    }}
                                                />
                                            </div>

                                            <div className="col-8 highlighted-item">
                                                <a
                                                    href="#"
                                                    tabIndex={0}
                                                    onClick={handleMediaClick(
                                                        media
                                                    )}
                                                >
                                                    {media.title}
                                                </a>
                                                <br />
                                                <span>
                                                    {mediaDate.toLocaleDateString()}
                                                </span>
                                            </div>
                                        </React.Fragment>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function getYoutubeId(url: URL | string | null): string | null {
    if (!url) return null;

    const regExp =
        /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.toString().match(regExp);

    return match && match[2].length === 11 ? match[2] : null;
}
